import { useState, useRef, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Send, Bot, User, Loader2, Target, Eye, PenTool, BarChart3, Zap,
  CheckCircle2, X, ArrowUp, ArrowDown,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";

interface DirectiveMessage {
  id: string;
  role: "user" | "agent";
  content: string;
  agent?: string;
  timestamp: Date;
  actions?: DirectiveAction[];
}

interface DirectiveAction {
  id: string;
  title: string;
  description: string;
  priority: "haute" | "moyenne" | "basse";
  validated: boolean | null;
}

const AGENTS = [
  { id: "all", name: "Tous les agents", icon: Bot, color: "hsl(var(--primary))" },
  { id: "veilleur", name: "Veilleur", icon: Eye, color: "hsl(var(--chart-1))" },
  { id: "qualificateur", name: "Qualificateur", icon: Target, color: "hsl(var(--chart-2))" },
  { id: "redacteur", name: "Rédacteur", icon: PenTool, color: "hsl(var(--chart-3))" },
  { id: "analyste", name: "Analyste", icon: BarChart3, color: "hsl(var(--chart-4))" },
  { id: "prospecteur", name: "Prospecteur", icon: Zap, color: "hsl(var(--chart-5))" },
];

const priorityConfig = {
  haute: { label: "🔴 Haute", className: "border-red-500/30 text-red-400 bg-red-500/10" },
  moyenne: { label: "🟡 Moyenne", className: "border-amber-500/30 text-amber-400 bg-amber-500/10" },
  basse: { label: "🟢 Basse", className: "border-emerald-500/30 text-emerald-400 bg-emerald-500/10" },
};

export function AgentDirectives() {
  const { user } = useAuth();
  const [messages, setMessages] = useState<DirectiveMessage[]>([
    {
      id: "welcome",
      role: "agent",
      content: "**Bienvenue dans l'espace Directives.**\n\nJe suis à votre écoute. Vous pouvez ici :\n\n- **Fixer des objectifs** pour un ou tous les agents\n- **Décrire vos attentes** et priorités business\n- **Ajuster les stratégies** de prospection, qualification ou rédaction\n\nChoisissez un agent ou adressez-vous à toute l'équipe. Je vous proposerai des actions concrètes que vous pourrez valider ou ajuster.",
      agent: "all",
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState("");
  const [selectedAgent, setSelectedAgent] = useState("all");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const parseActions = (text: string): { clean: string; actions: DirectiveAction[] } => {
    const actions: DirectiveAction[] = [];
    const clean = text.replace(/ACTION:\s*(.+?)\s*\|\s*(.+?)\s*\|\s*(Haute|Moyenne|Basse)/gi, (_, title, desc, prio) => {
      actions.push({
        id: crypto.randomUUID(),
        title: title.trim(),
        description: desc.trim(),
        priority: prio.toLowerCase() as "haute" | "moyenne" | "basse",
        validated: null,
      });
      return "";
    });
    return { clean: clean.trim(), actions };
  };

  const handleSend = async () => {
    if (!input.trim() || !user || sending) return;
    const userMsg: DirectiveMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: input.trim(),
      agent: selectedAgent,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setSending(true);

    try {
      const agentName = AGENTS.find((a) => a.id === selectedAgent)?.name || "Tous";
      const res = await fetch(`${API_URL}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include" as RequestCredentials,
        body: JSON.stringify({
          message: `[Directive NanoCorp → Agent ${agentName}] ${input.trim()}\n\nIMPORTANT: Pour chaque action proposée, utilise ce format:\nACTION: Titre | Description | Priorité (Haute/Moyenne/Basse)\nPropose 2-4 actions max, concis et orienté résultats.`,
          module: "creation",
        }),
      });
      if (!res.ok) throw new Error("Erreur API");
      const data = await res.json();

      const rawResponse = data?.response || "Je n'ai pas pu traiter votre demande.";
      const { clean, actions } = parseActions(rawResponse);

      const agentMsg: DirectiveMessage = {
        id: crypto.randomUUID(),
        role: "agent",
        content: clean || "Voici mes propositions :",
        agent: selectedAgent,
        timestamp: new Date(),
        actions: actions.length > 0 ? actions : undefined,
      };
      setMessages((prev) => [...prev, agentMsg]);
    } catch (err: any) {
      toast.error("Erreur de communication avec les agents");
    } finally {
      setSending(false);
    }
  };

  const handleValidateAction = (msgId: string, actionId: string, validated: boolean) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === msgId
          ? { ...m, actions: m.actions?.map((a) => (a.id === actionId ? { ...a, validated } : a)) }
          : m
      )
    );
    toast.success(validated ? "Action validée ✓" : "Action rejetée");
  };

  const handleChangePriority = (msgId: string, actionId: string) => {
    const cycle: ("haute" | "moyenne" | "basse")[] = ["basse", "moyenne", "haute"];
    setMessages((prev) =>
      prev.map((m) =>
        m.id === msgId
          ? {
              ...m,
              actions: m.actions?.map((a) => {
                if (a.id !== actionId) return a;
                const idx = cycle.indexOf(a.priority);
                return { ...a, priority: cycle[(idx + 1) % 3] };
              }),
            }
          : m
      )
    );
  };

  const agentMeta = AGENTS.find((a) => a.id === selectedAgent);

  return (
    <div className="flex flex-col h-[calc(100vh-220px)] min-h-[400px]">
      {/* Agent selector */}
      <div className="flex items-center gap-3 mb-3">
        <Select value={selectedAgent} onValueChange={setSelectedAgent}>
          <SelectTrigger className="w-[200px] h-9 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {AGENTS.map((a) => {
              const Icon = a.icon;
              return (
                <SelectItem key={a.id} value={a.id}>
                  <div className="flex items-center gap-2">
                    <Icon className="h-3.5 w-3.5" style={{ color: a.color }} />
                    <span>{a.name}</span>
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        <p className="text-[10px] text-muted-foreground hidden md:block">
          Fixez vos objectifs, décrivez vos attentes et validez les propositions
        </p>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-auto space-y-3 pr-1">
        {messages.map((msg) => {
          const isUser = msg.role === "user";
          const agent = AGENTS.find((a) => a.id === msg.agent);
          const AgentIcon = agent?.icon || Bot;

          return (
            <div key={msg.id} className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : ""}`}>
              <div
                className="h-7 w-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                style={{
                  background: isUser ? "hsl(var(--primary) / 0.15)" : `${agent?.color || "hsl(var(--primary))"}20`,
                }}
              >
                {isUser ? (
                  <User className="h-3.5 w-3.5 text-primary" />
                ) : (
                  <AgentIcon className="h-3.5 w-3.5" style={{ color: agent?.color }} />
                )}
              </div>
              <div className={`flex-1 max-w-[85%] ${isUser ? "text-right" : ""}`}>
                <Card className={`p-3 ${isUser ? "bg-primary/10 border-primary/20" : "bg-card border-border/50"}`}>
                  <div className="text-xs prose prose-sm prose-invert max-w-none [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:mb-2 [&_li]:text-xs [&_strong]:text-foreground text-foreground/90">
                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                  </div>

                  {/* Action cards */}
                  {msg.actions && msg.actions.length > 0 && (
                    <div className="mt-3 space-y-2 border-t border-border/30 pt-3">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Propositions d'actions
                      </p>
                      {msg.actions.map((action) => {
                        const prio = priorityConfig[action.priority];
                        return (
                          <div
                            key={action.id}
                            className={`p-2.5 rounded-lg border transition-all ${
                              action.validated === true
                                ? "border-emerald-500/30 bg-emerald-500/5"
                                : action.validated === false
                                ? "border-red-500/20 bg-red-500/5 opacity-50"
                                : "border-border/50 bg-muted/20"
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-foreground">{action.title}</p>
                                <p className="text-[10px] text-muted-foreground mt-0.5">{action.description}</p>
                              </div>
                              <Badge
                                variant="outline"
                                className={`text-[9px] px-1.5 cursor-pointer shrink-0 ${prio.className}`}
                                onClick={() => handleChangePriority(msg.id, action.id)}
                              >
                                {prio.label}
                              </Badge>
                            </div>
                            {action.validated === null && (
                              <div className="flex gap-1.5 mt-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-6 text-[10px] gap-1 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                                  onClick={() => handleValidateAction(msg.id, action.id, true)}
                                >
                                  <CheckCircle2 className="h-3 w-3" /> Valider
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-6 text-[10px] gap-1 border-red-500/30 text-red-400 hover:bg-red-500/10"
                                  onClick={() => handleValidateAction(msg.id, action.id, false)}
                                >
                                  <X className="h-3 w-3" /> Rejeter
                                </Button>
                              </div>
                            )}
                            {action.validated === true && (
                              <p className="text-[10px] text-emerald-400 mt-1.5 flex items-center gap-1">
                                <CheckCircle2 className="h-3 w-3" /> Action validée — en cours d'application
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card>
                <p className="text-[9px] text-muted-foreground mt-1">
                  {msg.timestamp.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
            </div>
          );
        })}
        {sending && (
          <div className="flex gap-2.5">
            <div className="h-7 w-7 rounded-lg flex items-center justify-center bg-primary/10">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
            </div>
            <Card className="p-3 bg-card border-border/50">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="animate-pulse">Les agents analysent votre directive...</span>
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="mt-3 flex gap-2">
        <Textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder={`Décrivez vos objectifs à ${agentMeta?.name || "l'équipe"}...`}
          className="min-h-[44px] max-h-[100px] text-xs resize-none"
          rows={1}
        />
        <Button onClick={handleSend} disabled={!input.trim() || sending} size="icon" className="h-[44px] w-[44px] shrink-0">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}
